import React, { useRef, useState } from 'react';
import { reduxForm, Form } from 'redux-form';
import { connect } from 'react-redux';
import { compose, pure } from 'recompose';
import { analytics, Track, validation } from 'angieslist-app-platform';
import { Col, Row, ReduxForm } from 'angieslist-react-components';
import { defaultPageProps } from '../../../../app/browser/analyticsDefaultProperties';
import { itemClicked } from '../../../../app/browser/analyticsHelpers';
import RecommendedField from '../../../common/recommendedField';
import { goToError } from './utils/Utils';
import './ServiceDetails.scss';

export type Props = {
    handleSubmit: any,
    serviceDetailsForm: any,
}

const styles = {
  textareas: {
    height: '85px',
  },
  padder: {
    padding: '1.4em',
  },
  textbox: {
    maxWidth: '15em',
  },
};

const validationRules = {
  // Add validation for service categories
};


export const ServiceDetails = ({
  handleSubmit,
  serviceDetailsForm: {
    values: { seniorDiscount },
  },
}: Props) => {
  const targetParagraphRef = useRef(null);
  const [serviceWasFocused, setServiceWasFocused] = useState(false);

  const focusOnParagraph = () => {
    if (window.location.href.indexOf('additional') > 0
      && targetParagraphRef.current && !serviceWasFocused) {
      targetParagraphRef.current.focus();
      setServiceWasFocused(true);
    }
  };

  return (
    <div id="Service-Details--Container">
      <div className="page-name">Service details</div>
      <Form
        onSubmit={handleSubmit}
        onSubmitFailed={goToError()}
        name="serviceDetailsForm"
      >
        <Row>
          <Col style={styles.padder}>
            <RecommendedField class="show-mobile" />
            <div style={{ display: 'flex' }}>
              <p className="title">Services highlights</p>
              <RecommendedField class="margin-property" />
            </div>
            <p className="subsection-helper-text">
              Select all that apply to your business:
            </p>
            <div>
              <Track>
                <ReduxForm.Input
                  id="Service-Details--MemOffering-Emergency-service"
                  name="emergencyService"
                  type="checkbox"
                  label="Offers emergency service"
                />
              </Track>
              <p className="checkbox-helper-text">We accept urgent requests outside normal business hours.</p>
            </div>
            <div>
              <Track>
                <ReduxForm.Input
                  name="COVID-19-VideoCalling"
                  id="Service-Details--HS-Video-Calling"
                  type="checkbox"
                  label="Video or phone estimates"
                />
              </Track>
              <p className="checkbox-helper-text">We offer convenient estimates over video or phone.</p>
            </div>
            <div>
              <p className="subtitle">
                Do you offer free estimates?
              </p>
              <Track>
                <ReduxForm.Input
                  id="Service-Details--MemOffering-Free-Estimates"
                  name="freeEstimates"
                  type="radio"
                  value="true"
                  label="Yes — we don’t charge for an estimate, including travel to site."
                />
              </Track>
              <Track>
                <ReduxForm.Input
                  id="Service-Details--MemOffering-Free-Estimates-None"
                  name="freeEstimates"
                  type="radio"
                  value="false"
                  label="No — we charge for estimates (or we charge for travel to provide one)."
                />
              </Track>
            </div>
            <div>
              <p className="subtitle">
                Do you offer any warranty?
              </p>
              <Track>
                <ReduxForm.Input
                  id="Service-Details--MemOffering-Warranties-Offered"
                  name="warranty"
                  type="radio"
                  value="true"
                  label="Yes"
                />
              </Track>
              <Track>
                <ReduxForm.Input
                  id="Service-Details--MemOffering-Warranties-Offered-None"
                  name="warranty"
                  type="radio"
                  value="false"
                  label="No"
                />
              </Track>
            </div>
            <div style={{ 'margin-bottom': '0px' }}>
              <p className="subtitle">
                Do you offer a senior discount?
              </p>
              <Track>
                <ReduxForm.Input
                  id="Service-Details--Senior-Discount-Offered"
                  name="seniorDiscount"
                  type="radio"
                  value="true"
                  label="Yes"
                />
              </Track>
              <ReduxForm.Input
                id="Service-Details--Senior-Discount-Amount"
                name="seniorDiscountAmount"
                type="text"
                placeholder="Discount details"
                disabled={seniorDiscount === 'false'}
                style={styles.textbox}
              />
              <Track>
                <ReduxForm.Input
                  id="Service-Details--Senior-Discount-None"
                  name="seniorDiscount"
                  type="radio"
                  value="false"
                  label="No"
                />
              </Track>
            </div>
          </Col>
        </Row>
        <hr className="divider" />
        <Row>
          <Col style={styles.padder}>
            <RecommendedField class="show-mobile" />
            <div style={{ display: 'flex' }}>
              <p className="title">Payment details</p>
              <RecommendedField class="margin-property" />
            </div>
            <p className="subsection-helper-text">
              Select all that apply to your business:
            </p>
            <Track>
              <ReduxForm.Input
                id="Service-Details--Payment-FinancingAvailable"
                name="financingAvailable"
                type="checkbox"
                label="Financing available"
              />
            </Track>
            <Track>
              <ReduxForm.Input
                id="Service-Details--Payment-Credit-Card"
                name="creditCard"
                type="checkbox"
                label="Credit card"
              />
            </Track>
            <Track>
              <ReduxForm.Input
                id="Service-Details--Payment-Venmo"
                name="venmo"
                type="checkbox"
                label="Venmo"
              />
            </Track>
            <Track>
              <ReduxForm.Input
                id="Service-Details--Payment-Zelle"
                name="zelle"
                type="checkbox"
                label="Zelle"
              />
            </Track>
            <Track>
              <ReduxForm.Input
                id="Service-Details--Payment-PayPal"
                name="payPal"
                type="checkbox"
                label="PayPal"
              />
            </Track>
            <Track>
              <ReduxForm.Input
                id="Service-Details--Payment-Check"
                name="check"
                type="checkbox"
                label="Check"
              />
            </Track>
          </Col>
        </Row>
        <hr className="divider" />
        <Row>
          <Col style={styles.padder}>
            <p className="title">Eco-friendly accreditations</p>
            <p className="subsection-helper-text">
              Select all that apply to your business:
            </p>
            <Track>
              <ReduxForm.Input
                id="Service-Details--EcoFriendly-LEED-Accredited"
                name="LEEDAccredited"
                type="checkbox"
                label="LEED-accredited professional"
              />
            </Track>
            <Track>
              <ReduxForm.Input
                id="Service-Details--EcoFriendly-Energy-Star"
                name="energyStarPartner"
                type="checkbox"
                label="Energy Star partner"
              />
            </Track>
            <Track>
              <ReduxForm.Input
                id="Service-Details--EcoFriendly-EPA-Lead-Safe"
                name="EPALeadSafeCertified"
                type="checkbox"
                label="EPA lead-safe certified"
              />
            </Track>
            <Track>
              <ReduxForm.Input
                id="Service-Details--EcoFriendly-Green-Products"
                name="greenProductWorkPracticeProfessional"
                type="checkbox"
                label="Use green products or work"
              />
            </Track>
          </Col>
        </Row>
        <hr className="divider" />
        <Row>
          <Col style={styles.padder}>
            <p
              className="title"
              tabIndex="0" /* eslint-disable-line */
              ref={targetParagraphRef}
              onLoad={focusOnParagraph()}
            >
              Additional service details
            </p>
            <div>
              <p style={{ 'margin-top': '20px' }}>
                Are there any additional service details or
                areas of expertise that you want to share?
              </p>
              <Track>
                <ReduxForm.Input
                  id="Service-Details--Services-Offered"
                  name="servicesOffered"
                  type="textarea"
                  style={styles.textareas}
                />
              </Track>
            </div>
            <div>
              <p style={{ 'margin-top': '20px' }}>
                Are there any specific tasks you don’t offer within your service categories?
              </p>
              <Track>
                <ReduxForm.Input
                  id="Service-Details--Services-Not-Offered"
                  name="servicesNotOffered"
                  type="textarea"
                  style={styles.textareas}
                />
              </Track>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

const editServiceDetailsAnalytics = {
  page: {
    name: 'SP : My Business : Service details',
    properties: {
      ...defaultPageProps,
    },
  },
  elements: {
    'Service-Details--Services-Offered': itemClicked('SP : My Business : Service details', 'My Business : Services Offered'),
    'Service-Details--Services-Not-Offered': itemClicked('SP : My Business : Service details', 'My Business : Services Not Offered'),
    'Service-Details--Additional-Services-*': itemClicked('SP : My Business : Service details', 'My Business : Additional Services'),
    'Service-Details--MemOffering-*': itemClicked('SP : My Business : Service details', 'My Business : Mem Offerings'),
    'Service-Details--Payment-*': itemClicked('SP : My Business : Service details', 'My Business : Payment options'),
    'Service-Details--EcoFriendly-*': itemClicked('SP : My Business : Service details', 'My Business : Eco Accreditations'),
    'Service-Details--Senior-Discount-*': itemClicked('SP : My Business : Service details', 'My Business : Senior Discount'),
  },
};

export default compose(
  reduxForm({
    form: 'serviceDetailsForm',
    validate: values => validation.validate(values, validationRules).invalidAttrs,
    destroyOnUnmount: false,
  }),
  connect(state => ({
    serviceDetailsForm: state.form.serviceDetailsForm,
  })),
  analytics({
    ruleset: editServiceDetailsAnalytics,
  }),
  pure,
)(ServiceDetails);



// WEBPACK FOOTER //
// ./src/modules/profile/browser/components/ServiceDetails.js